class CartMotivator extends HTMLElement {
  constructor() {
    super();
    this.headerContainer = this.querySelector('.cart_motivator-header');
    this.labelContainer = this.querySelector('.cart_motivator-label');
    this.stepsContainer = this.querySelector('.cart_motivator-steps-container');
    this.steps = this.querySelectorAll('.cart_motivator-step');
    this.discountCodesDB = new SessionDiscountCodeStorage();
    this.stepsArray = [...this.steps].map((stepEl) => {
      const header = stepEl.getAttribute('data-header-text');
      const label = stepEl.getAttribute('data-label-text');
      const goalAmount =
        Number(stepEl.getAttribute('data-goal-amount')) || 10000;
      const goalType = stepEl.getAttribute('data-goal-type');
      const excludedCollections =
        JSON.parse(
          stepEl.getAttribute('data-excluded-collection-ids') ?? 'null',
        ) || [];
      const discountCode = stepEl.getAttribute('data-discount-code') || '';
      return {
        el: stepEl,
        header,
        label,
        goal_amount: goalAmount,
        goal_type: goalType,
        discount_code: discountCode?.trim()?.toUpperCase() || null,
        excluded_collections: excludedCollections,
      };
    });
    this.codesArray = this.stepsArray
      .map((step) => step.discount_code)
      .filter((code) => code !== null);
    this.stepsClasses = {
      target: 'cart_motivator-step-target',
      unlocked: 'cart_motivator-step-unlocked',
    };
    this.goalType = this.getAttribute('data-cart-goal-type') || 'total_price';
    this.initLabel = this.getAttribute('data-init-label');
    this.finalLabel = this.getAttribute('data-final-label');
    this.initCartObserver();
  }

  cartCheck = async () => {
    const cart = await this.getCart();
    if (!cart) {
      return;
    }

    let isTargetStep = false;
    let isPreviousAllowed = true;

    let lastCartAmount = {
      stepIndex: null,
      amount: null,
    };

    let defaultAmounts = {
      first: null,
      last: null,
    };

    let lastAllowedIndex = null;

    const discountAllocations = cart.discount_applications?.filter(
      (discountAllocation) => discountAllocation.type !== 'discount_code',
    );

    const cartCodes = cart.discount_applications
      ?.filter(
        (discountAllocation) => discountAllocation.type === 'discount_code',
      )
      .map((discountAllocation) => discountAllocation.title);

    const codeList = [...cartCodes];

    const { extra: nonAllowedCodes } = this.compareArrays({
      a: this.codesArray,
      b: codeList,
    });

    const hideNotAllowedSteps =
      nonAllowedCodes.length > 0 || discountAllocations.length > 0;

    const { stepsArray, removedStepsArray, stepsArrayReverse } =
      this.filterSteps({
        steps: this.stepsArray,
        excludeStepWithCodes: hideNotAllowedSteps,
      });

    removedStepsArray.forEach((stepItem) => {
      stepItem.el.classList.add('hide');
      const cartAmount = this.getCartAmount({
        cart: cart,
        excludedCollections: stepItem.excluded_collections,
        goalType: stepItem.goal_type,
      });

      if (
        cartAmount <= stepItem.goal_amount &&
        stepItem.discount_code &&
        stepItem.discount_code.length &&
        codeList.includes(stepItem.discount_code)
      ) {
        codeList.splice(codeList.indexOf(stepItem.discount_code), 1);
      }
    });

    // const excludedCodes = removedStepsArray
    //   .map((step) => step.discount_code)
    //   .filter((code) => code !== null);

    stepsArray.forEach((stepItem, stepItemIndex) => {
      stepItem.el.classList.remove('hide');
      const cartAmount = this.getCartAmount({
        cart: cart,
        excludedCollections: stepItem.excluded_collections,
        goalType: stepItem.goal_type,
      });
      if (cartAmount >= stepItem.goal_amount && isPreviousAllowed) {
        stepItem.el.classList.add(this.stepsClasses.unlocked);
        lastAllowedIndex = stepItemIndex;
        if (
          stepItem.discount_code &&
          stepItem.discount_code.length &&
          codeList.indexOf(stepItem.discount_code) < 0
        ) {
          codeList.push(stepItem.discount_code);
        }
      } else {
        if (stepItem.discount_code && stepItem.discount_code.length) {
          this.discountCodesDB?.register([
            {
              code: stepItem.discount_code,
              status: 'error-adding',
              metadata: {
                errorCount: 0,
              },
            },
          ]);
          if (codeList.indexOf(stepItem.discount_code) >= 0) {
            codeList.splice(codeList.indexOf(stepItem.discount_code), 1);
          }
        }
        isPreviousAllowed = false;
        stepItem.el.classList.remove(this.stepsClasses.unlocked);
        if (isTargetStep === false) {
          isTargetStep = true;
          const remainingAmount = this.convertToMoney({
            amount: stepItem.goal_amount - cartAmount,
            removeTrailingZeros: false,
          });
          this.headerContainer.innerHTML = stepItem.header.replace(
            '{amount}',
            remainingAmount,
          );
        }
      }

      if (defaultAmounts.first === null && stepItemIndex === 0) {
        defaultAmounts.first = cartAmount;
      }
      defaultAmounts.last = cartAmount;
    });

    stepsArrayReverse.forEach((stepItem, stepItemIndex) => {
      const cartAmount = this.getCartAmount({
        cart: cart,
        excludedCollections: stepItem.excluded_collections,
        goalType: stepItem.goal_type,
      });

      if (
        cartAmount <= stepItem.goal_amount &&
        lastCartAmount.amount === null
      ) {
        lastCartAmount.amount = cartAmount;
        lastCartAmount.stepIndex = stepItemIndex;
      }
    });

    if (isTargetStep === false) {
      this.headerContainer.innerHTML = this.finalLabel || '';
    }

    isTargetStep = false;

    stepsArrayReverse.forEach((stepItem) => {
      const cartAmount = this.getCartAmount({
        cart: cart,
        excludedCollections: stepItem.excluded_collections,
        goalType: stepItem.goal_type,
      });

      if (cartAmount >= stepItem.goal_amount && isTargetStep === false) {
        isTargetStep = true;
        this.labelContainer.innerHTML = stepItem.label;
      }
    });

    if (isTargetStep === false) {
      this.labelContainer.innerHTML = this.initLabel || '';
    }

    if (lastAllowedIndex === null) {
      lastCartAmount.stepIndex = 0;
      lastCartAmount.amount = defaultAmounts.first;
    } else if (lastAllowedIndex === stepsArray.length - 1) {
      lastCartAmount.stepIndex = stepsArray.length - 1;
      lastCartAmount.amount = defaultAmounts.last;
    }

    this.setProgressBarAmount({
      currentAmount: lastCartAmount.amount ?? 0,
      currentIndex: lastCartAmount.stepIndex ?? 0,
      stepsAmount: stepsArray.map((step) => step.goal_amount),
    });

    const { missing, extra } = this.compareArrays({
      a: cartCodes,
      b: codeList,
    });

    const codesListFound =
      this.discountCodesDB?.get([...cartCodes, ...extra]) || [];

    missing.forEach((code) => {
      const codeMatch = codesListFound.find(
        (codeFound) => codeFound && codeFound.code === code,
      );
      if (codeMatch && codeMatch.status === 'manually-added') {
        codeList.push(code);
      }
    });

    extra.forEach((code) => {
      const codeMatch = codesListFound.find(
        (codeFound) => codeFound && codeFound.code === code,
      );
      if (
        codeMatch &&
        (codeMatch.status === 'manually-removed' ||
          (codeMatch.status === 'error-adding' &&
            codeMatch.metadata?.errorCount > 1))
      ) {
        codeList.splice(codeList.indexOf(code), 1);
      }
    });

    // excludedCodes.forEach((code) => {
    //   if (!codeList.includes(code)) {
    //     return;
    //   }
    //   const codeMatch = codesListFound.find(
    //     (codeFound) => codeFound && codeFound.code === code,
    //   );
    //   if (codeMatch && codeMatch.status !== 'manually-added') {
    //     codeList.splice(codeList.indexOf(code), 1);
    //   }
    // });

    if (!this.arraysEqual({ a: cartCodes, b: codeList })) {
      const { missing: finalMissing, extra: finalNews } = this.compareArrays({
        a: cartCodes,
        b: codeList,
      });

      const updateCodesResponse = await this.updateCartCodes({
        codes: codeList,
      });

      if (updateCodesResponse !== null) {
        finalMissing.forEach((code) =>
          this.discountCodesDB?.register([
            { code: code, status: 'auto-removed' },
          ]),
        );
        finalNews.forEach((codeAdded) => {
          const targetItem = updateCodesResponse.find(
            ({ code }) => codeAdded === code,
          );
          const foundTargetItem = codesListFound.find(
            ({ code }) => code === codeAdded,
          );
          if (targetItem && targetItem.applicable) {
            this.discountCodesDB?.register([
              { code: codeAdded, status: 'auto-added' },
            ]);
            return;
          }
          this.discountCodesDB?.register([
            {
              code: codeAdded,
              status: 'error-adding',
              metadata: {
                errorCount: (foundTargetItem?.metadata?.errorCount || 0) + 1,
              },
            },
          ]);
        });
        document.dispatchEvent(new Event('cart:build'));
      }
    }
  };

  setProgressBarAmount = ({ currentAmount, currentIndex, stepsAmount }) => {
    const percentageAmount = ({ goals, currentAmount, currentIndex }) => {
      if (!Array.isArray(goals) || !goals.length) return 0;

      const segmentPercentage = 1 / goals.length;
      const maxPercentage = currentIndex / goals.length;
      const percentage = currentAmount / goals[currentIndex];

      const finalPercentage =
        (percentage * segmentPercentage + maxPercentage) * 100;

      return finalPercentage > 100 ? 100 : finalPercentage;
    };

    const percentage = percentageAmount({
      goals: stepsAmount,
      currentAmount,
      currentIndex,
    });

    const stepsReduce = this.getStepSize().width;

    this.stepsContainer.style = `--progressBarAmount: calc(${percentage}% - ${stepsReduce}px)`;
  };

  getStepSize = () => {
    return {
      width:
        [...this.steps]?.at(0)?.querySelector('.cart_motivator-step-inner')
          .offsetWidth || 22,
    };
  };

  getCart = async () => {
    try {
      const rawResponse = await fetch('/').then((res) => res.text());
      if (!rawResponse) return null;
      const parser = new DOMParser();
      const docu = parser.parseFromString(rawResponse, 'text/html');
      return JSON.parse(
        docu.querySelector('[data-ggm-custom-cart-json]').textContent,
      );
    } catch (e) {
      return null;
    }
  };

  updateCartCodes = async ({ codes }) => {
    if (!codes) {
      return null;
    }
    const formData = { discount: codes.join(',') || '' };
    try {
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      }).then((res) => res.json());

      return updateResponse?.discount_codes || null;
    } catch (e) {
      return null;
    }
  };

  getCartAmount = ({ cart, excludedCollections, goalType }) => {
    if (!cart) {
      return 0;
    }

    switch (goalType) {
      case 'original_total_price':
        return cart.items.reduce((acc, item) => {
          if (
            item.collection_ids.some((collectionId) =>
              excludedCollections.includes(collectionId),
            )
          ) {
            return acc;
          }
          return acc + item.original_line_price;
        }, 0);
      case 'total_price':
      default:
        return cart.items.reduce((acc, item) => {
          if (
            item.collection_ids.some((collectionId) =>
              excludedCollections.includes(collectionId),
            )
          ) {
            return acc;
          }
          return acc + item.line_price;
        }, 0);
    }
  };

  convertToMoney = ({ amount, removeTrailingZeros = false }) => {
    const finalAmount =
      (typeof amount === 'string' ? Number(amount) : amount) / 100;

    const currency = window?.Shopify?.currency?.active || 'USD';

    const localeString = `${window?.Shopify?.locale || 'en'}-${window?.Shopify?.country || 'US'}`;

    const fixedAmount = Number(finalAmount.toFixed(2));

    let formattedAmount = fixedAmount.toLocaleString(localeString, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: removeTrailingZeros ? 0 : 2,
      maximumFractionDigits: removeTrailingZeros ? 2 : 2,
    });

    if (removeTrailingZeros) {
      const decimalSeparator = (1.1).toLocaleString(localeString).charAt(1);
      const amount = formattedAmount.match(/-?\d+(\.\d+)?/)?.[0] || '0';
      if (amount.length === 0) {
        formattedAmount += `${decimalSeparator}00`;
      } else {
        formattedAmount = formattedAmount
          .replace(new RegExp(`\\${decimalSeparator}0+$`), '')
          .replace(new RegExp(`\\${decimalSeparator}$`), '');
      }
    }

    return formattedAmount;
  };

  initCartObserver = () => {
    const cartObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((rawEntry) => {
        const entry = rawEntry.toJSON();
        const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(
          entry.initiatorType,
        );
        const regex = /\/cart(\/(?!\.js\b)[^ ]*|$)/;
        const isCartChangeRequest = regex.test(entry.name);
        if (isValidRequestType && isCartChangeRequest) {
          this.cartCheck();
        }
      });
    });

    cartObserver.observe({ entryTypes: ['resource'] });
    this.cartCheck();
  };

  arraysEqual = ({ a, b }) => {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((val, i) => val === b[i])
    );
  };

  compareArrays = ({ a, b }) => {
    const missing = a.filter((item) => !b.includes(item));
    const extra = b.filter((item) => !a.includes(item));
    return { missing, extra };
  };

  filterSteps = ({ steps, excludeStepWithCodes = false }) => {
    if (!excludeStepWithCodes) {
      return {
        stepsArray: steps,
        removedStepsArray: [],
        stepsArrayReverse: [...steps].reverse(),
      };
    }

    const stepsArray = steps.filter((step) => step.discount_code === null);
    const removedStepsArray = steps.filter(
      (step) => step.discount_code !== null,
    );

    return {
      stepsArray,
      removedStepsArray,
      stepsArrayReverse: [...stepsArray].reverse(),
    };
  };
}

if (!customElements.get('cart-motivator')) {
  customElements.define('cart-motivator', CartMotivator);
}
